/**
 * RFC-0089: Yjs-backed canvas state hooks.
 * RFC-0319: Per-field YDoc node data (source of truth).
 *
 * These hooks observe a Y document's "nodes", "edges", and "viewport" maps
 * and convert them to ReactFlow-compatible data structures. They also provide
 * write functions for updating positions and viewport back into the Y doc.
 *
 * The backend computes node positions (via layout.py) and writes them to YDoc.
 * The frontend reads positions directly from YDoc — no client-side layout needed.
 * The frontend writes only x, y, tainted (on drag) and viewport (on pan/zoom).
 *
 * Per-field reads (RFC-0319): individual in_ and out_ prefixed keys are read
 * directly from each Y.Map node entry with schema-driven deserialization.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import * as Y from 'yjs'
import type { Edge, Node, ReactFlowInstance, Viewport } from 'reactflow'
import { useQueryClient } from '@tanstack/react-query'
import { CANVAS_HIDDEN_FE_TYPES, FE_TYPE_TO_BACKEND, YDOC_STRUCTURAL_KEYS, flushYDocInputFromEdgeRefs, readYDocNodeDataFields, writeYDocNodeDataFields, createDebug, type YDocInputFlushEdgeRef, type YDocInputFlushNode, type YDocInputFlushResult, type YDocWriteRecord, type YDocWriteValue, type YDocValue } from '@ponpon/api-client'
import {
  deriveGroupedNodeLayout,
} from '../components/canvas-v2/nodes/GroupNode/GroupNode'
import { queryKeys } from '../api/hooks'
const log = createDebug('dw:canvas:ydoc')
const statusLog = createDebug('dw:canvas:status')
const ydocWriteLog = createDebug('dw:ydoc-write:ydoc-write')
const lifecycleLog = createDebug('dw:ydoc:lifecycle')
const layoutLog = createDebug('dw:layout:position')
const libraryImportLog = createDebug('dw:library-import:node-status')
const costInvalidateLog = createDebug('dw:cost-summary:invalidate')

type YDocMap = Y.Map<YDocValue>
type YDocMapTable = Y.Map<YDocMap>
type YDocEvent = Y.YEvent<YDocMap>
export type ReactFlowFieldValue = YDocWriteValue
type NodeDataUpdates = YDocWriteRecord
export type ReactFlowNodeData = {
  [key: string]: ReactFlowFieldValue
}
export type ReactFlowCanvasNode = Node<ReactFlowNodeData, string | undefined>
export type ReactFlowCanvasEdgeData = { toIndex?: number; fromIndex?: number }
export type ReactFlowCanvasEdge = Edge<ReactFlowCanvasEdgeData>

function yString(map: YDocMap, key: string, fallback = ''): string {
  const value = map.get(key)
  return typeof value === 'string' ? value : fallback
}

function yOptionalString(map: YDocMap, key: string): string | undefined {
  const value = map.get(key)
  return typeof value === 'string' ? value : undefined
}

function yNumber(map: YDocMap, key: string, fallback = 0): number {
  const value = map.get(key)
  return typeof value === 'number' ? value : fallback
}

function yOptionalNumber(map: YDocMap, key: string): number | undefined {
  const value = map.get(key)
  return typeof value === 'number' ? value : undefined
}

function compareTargetIndex(left: number | undefined, right: number | undefined): number {
  if (left === undefined && right === undefined) return 0
  if (left === undefined) return 1
  if (right === undefined) return -1
  return left - right
}

type TargetPortRef = {
  target: string
  targetHandle?: string
}

function rememberTargetPort(
  ports: TargetPortRef[],
  target: string,
  targetHandle: string | undefined,
): void {
  const exists = ports.some(
    (port) => port.target === target && port.targetHandle === targetHandle,
  )
  if (!exists) ports.push({ target, targetHandle })
}

function reindexTargetPortEdges(
  yEdges: YDocMapTable,
  target: string,
  targetHandle: string | undefined,
): void {
  const siblings: Array<{ edge: YDocMap; toIndex: number | undefined }> = []
  yEdges.forEach((yedge: YDocMap) => {
    const edgeTarget = yString(yedge, 'target')
    const edgeTargetHandle = yOptionalString(yedge, 'targetHandle')
    if (edgeTarget === target && edgeTargetHandle === targetHandle) {
      siblings.push({ edge: yedge, toIndex: yOptionalNumber(yedge, 'toIndex') })
    }
  })
  siblings
    .sort((left, right) => compareTargetIndex(left.toIndex, right.toIndex))
    .forEach((item, index) => item.edge.set('toIndex', index))
}

function yBoolean(map: YDocMap, key: string, fallback = false): boolean {
  const value = map.get(key)
  return typeof value === 'boolean' ? value : fallback
}

function normalizeReactFlowFieldValue(value: ReactFlowFieldValue): ReactFlowFieldValue {
  if (value === null || value === undefined) return value
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
  if (value instanceof Y.Map || value instanceof Y.Array) return value
  if (typeof File !== 'undefined' && value instanceof File) return value
  if (typeof value === 'object') return value
  return String(value)
}

function normalizeNodeDataUpdates(updates: NodeDataUpdates): NodeDataUpdates {
  const normalized: NodeDataUpdates = {}
  for (const [key, value] of Object.entries(updates)) {
    normalized[key] = normalizeReactFlowFieldValue(value)
  }
  return normalized
}

/**
 * Read node data from a Y.Map entry using per-field keys.
 *
 * RFC-0319: All non-structural keys are read as node data fields.
 * This includes schema-declared input/output fields and legacy state fields.
 * Data-field deserialization is schema-driven via generated YDoc readers.
 */
export function readNodeDataFromYMap(ynode: YDocMap): Record<string, YDocValue> {
  const feType = yString(ynode, 'type')
  const dataRaw = readYDocNodeDataFields(ynode, toBackendType(feType))
  const statusVal = yOptionalString(ynode, 'status')
  statusLog('read nodeType=%s fieldCount=%d status=%s', feType, Object.keys(dataRaw).length, statusVal ?? 'undefined')

  return dataRaw
}

const HISTORY_POINTER_FIELDS: Record<string, string> = {
  out_generated_image: 'out_image',
  out_generated_video: 'out_video',
}

const DESIGNER_IMAGE_FIELDS = new Set([
  'out_character_sheet',
  'out_location_image',
  'out_object_image',
])

function isRecord(value: ReactFlowFieldValue): value is NodeDataUpdates {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseJsonString(value: ReactFlowFieldValue): ReactFlowFieldValue {
  if (typeof value !== 'string') return value
  try { return JSON.parse(value) } catch { return value }
}

function fieldValue(value: ReactFlowFieldValue, key: string): ReactFlowFieldValue {
  const parsed = parseJsonString(value)
  if (parsed instanceof Y.Map) return parsed.get(key)
  if (isRecord(parsed)) return parsed[key]
  return undefined
}

function toArray(value: ReactFlowFieldValue): ReactFlowFieldValue[] {
  const parsed = parseJsonString(value)
  if (Array.isArray(parsed)) return parsed
  if (parsed instanceof Y.Array) return parsed.toArray()
  return []
}

function toIndex(value: ReactFlowFieldValue): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

function entryLocalPath(entry: ReactFlowFieldValue): string | undefined {
  const result = fieldValue(entry, 'result')
  const localPath = fieldValue(result, 'local_path')
  return typeof localPath === 'string' && localPath.length > 0 ? localPath : undefined
}

function selectedLocalPathFromHistory(value: ReactFlowFieldValue, fallback?: ReactFlowFieldValue): string | undefined {
  const pickedIndex = toIndex(fieldValue(value, 'picked_index')) ?? toIndex(fieldValue(fallback, 'picked_index')) ?? 0
  const history = toArray(fieldValue(value, 'history'))
  const fallbackHistory = toArray(fieldValue(fallback, 'history'))

  for (const candidates of [history, fallbackHistory]) {
    if (pickedIndex >= 0 && pickedIndex < candidates.length) {
      const selectedPath = entryLocalPath(candidates[pickedIndex])
      if (selectedPath) return selectedPath
    }
  }

  return entryLocalPath(value) ?? entryLocalPath(fallback)
}

function nestedFieldValue(value: ReactFlowFieldValue, path: string[]): ReactFlowFieldValue {
  return path.reduce<ReactFlowFieldValue>((current, key) => fieldValue(current, key), value)
}

function selectedLocalPathFromDesignerAsset(value: ReactFlowFieldValue, fallback?: ReactFlowFieldValue): string | undefined {
  return selectedLocalPathFromHistory(
    nestedFieldValue(value, ['images', 'main']),
    nestedFieldValue(fallback, ['images', 'main']),
  )
}

function withSynchronizedVariantPointers(
  updates: NodeDataUpdates,
  ynode: YDocMap,
  nodeId: string,
): NodeDataUpdates {
  const synchronized = { ...updates }

  for (const [historyField, pointerField] of Object.entries(HISTORY_POINTER_FIELDS)) {
    if (!(historyField in updates)) continue
    const selectedPath = selectedLocalPathFromHistory(updates[historyField], ynode.get(historyField))
    if (selectedPath) {
      synchronized[pointerField] = selectedPath
      ydocWriteLog('sync pointer nodeId=%s source=%s pointer=%s path=%s', nodeId, historyField, pointerField, selectedPath)
    } else {
      ydocWriteLog('sync pointer skipped nodeId=%s source=%s reason=missing-selected-local-path', nodeId, historyField)
    }
  }

  for (const designerField of DESIGNER_IMAGE_FIELDS) {
    if (!(designerField in updates)) continue
    const selectedPath = selectedLocalPathFromDesignerAsset(updates[designerField], ynode.get(designerField))
    if (selectedPath) {
      synchronized.out_image = selectedPath
      ydocWriteLog('sync pointer nodeId=%s source=%s pointer=out_image path=%s', nodeId, designerField, selectedPath)
    } else {
      ydocWriteLog('sync pointer skipped nodeId=%s source=%s reason=missing-selected-local-path', nodeId, designerField)
    }
  }

  return synchronized
}

/** Convert frontend camelCase node type to backend snake_case.
 *
 * Uses the generated FE_TYPE_TO_BACKEND lookup table (from schemas) with a
 * regex fallback for any types not in the table. The lookup is needed because
 * naive camelCase→snake_case doesn't work for all types.
 */
export function toBackendType(feType: string): string {
  return FE_TYPE_TO_BACKEND[feType] ?? feType.replace(/([A-Z])/g, '_$1').toLowerCase()
}

/**
 * Node as stored in the Y document (backend-computed position included).
 */
export interface BackendNode {
  id: string
  type: string
  data: {
    status: string
    nodeType?: string
    [key: string]: ReactFlowFieldValue
  }
  position: { x: number; y: number }
}

// ---------------------------------------------------------------------------
// useYCanvas: Y doc → ReactFlow nodes/edges (single observer)
// ---------------------------------------------------------------------------

interface UseYCanvasOptions {
  ydoc: Y.Doc
  synced: boolean
  /** ReactFlow setNodes */
  setNodes: (nodes: ReactFlowCanvasNode[] | ((prev: ReactFlowCanvasNode[]) => ReactFlowCanvasNode[])) => void
  /** ReactFlow setEdges */
  setEdges: (edges: ReactFlowCanvasEdge[] | ((prev: ReactFlowCanvasEdge[]) => ReactFlowCanvasEdge[])) => void
  /** Ref to current ReactFlow nodes */
  nodesRef: React.MutableRefObject<ReactFlowCanvasNode[]>
  /** Ref to current ReactFlow edges */
  edgesRef: React.MutableRefObject<ReactFlowCanvasEdge[]>
  /** Whether backend is connected and Y doc is synced */
  isBackendConnected: boolean
  /** Ref to skip rebuild during undo/redo */
  isUndoRedo: React.MutableRefObject<boolean>
  /** ReactFlow instance ref for auto-focus */
  reactFlowInstance: React.MutableRefObject<ReactFlowInstance<ReactFlowNodeData, ReactFlowCanvasEdgeData> | null>
  /** When set, skip YDoc nodes whose type is not in this set */
  knownNodeTypes?: ReadonlySet<string>
  /** Selection ref — source of truth for which nodes are selected */
  selectedNodeIdsRef: React.RefObject<Set<string>>
  /** Current project ID — used to invalidate the cost-summary React Query
   *  entry when a non-director node terminalizes, so the Sparks & Usage
   *  modal's Node Breakdown reflects fresh data without a page refresh. */
  projectId?: string
}

interface UseYCanvasResult {
  /** True after the first rebuild has populated nodes into React state */
  ready: boolean
  /** Number of non-director nodes with status=running in YDoc */
  ydocRunningCount: number
  /** Number of non-director nodes with status=pending_execution in YDoc */
  ydocQueuedCount: number
  /** Write a node's position (and optionally tainted flag) back to the Y document */
  writeNodePosition: (nodeId: string, x: number, y: number, tainted?: boolean) => void
  /** Pre-create a node in the Y doc so backend reconciliation preserves its position */
  addNodeToYDoc: (nodeId: string, nodeType: string, label: string, x: number, y: number, data?: NodeDataUpdates, tainted?: boolean, parentId?: string) => void
  /** Remove a node from the Y doc (cleanup on deletion) */
  removeNodeFromYDoc: (nodeId: string) => void
  /** Patch per-field in_/out_ keys on a node's YDoc entry */
  writeNodeData: (nodeId: string, updates: NodeDataUpdates) => void
  /** Flush one downstream input from connected source edge refs via generated field writers */
  flushNodeInputFromEdgeRefs: (targetId: string, targetHandle: string, nodes: readonly YDocInputFlushNode[], edgeRefs: readonly YDocInputFlushEdgeRef[]) => YDocInputFlushResult | undefined
  /** Write measured dimensions back to YDoc so backend layout knows real sizes */
  writeNodeDimensions: (nodeId: string, width: number, height: number) => void
  /** Set or clear the top-level parentNode field on a ynode (sets the ReactFlow parentId) */
  writeNodeParent: (nodeId: string, parentId: string | null) => void
  /** Add an edge to YDoc */
  addEdgeToYDoc: (edge: { source: string; target: string; sourceHandle?: string; targetHandle?: string; toIndex?: number; fromIndex?: number; type?: string }) => void
  /** Remove an edge from YDoc by its ID */
  removeEdgeFromYDoc: (edgeId: string) => void
  /** Remove edges from YDoc matching source/target/ports */
  removeEdgesFromYDocByPorts: (edges: Array<{ source: string; target: string; sourceHandle?: string; targetHandle?: string }>) => void
}

/**
 * Stamp node.selected from the selection set ref. Used during YDoc rebuilds to
 * preserve the user's current selection without scanning prevNodes.
 */
function applySelection(
  nodes: ReactFlowCanvasNode[],
  selectedIdsRef: React.RefObject<Set<string>>,
): ReactFlowCanvasNode[] {
  const selectedIds = selectedIdsRef.current
  if (!selectedIds || selectedIds.size === 0) return nodes
  return nodes.map(n => selectedIds.has(n.id) ? { ...n, selected: true } : n)
}

export function useYCanvas({
  ydoc,
  synced,
  setNodes,
  setEdges,
  nodesRef,
  edgesRef,
  isBackendConnected,
  isUndoRedo,
  reactFlowInstance,
  knownNodeTypes,
  selectedNodeIdsRef,
  projectId,
}: UseYCanvasOptions): UseYCanvasResult {
  // True after the first rebuild has populated nodes — used to gate canvas visibility
  const [ready, setReady] = useState(false)
  // Running non-director node count from YDoc.
  const [ydocRunningCount, setYdocRunningCount] = useState(0)
  // Queued (pending_execution) non-director node count from YDoc
  const [ydocQueuedCount, setYdocQueuedCount] = useState(0)

  // Cost-summary cache invalidation — trailing-edge debounce so a batch
  // of N panels terminalizing back-to-back collapses to ONE refetch
  // after StateManager has had a chance to commit all of them.
  const queryClient = useQueryClient()
  const costInvalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const costInvalidateBatchedRef = useRef(0)

  // Keep refs to Y maps for write helpers
  const yNodesRef = useRef<YDocMapTable | null>(null)

  // Track whether this is the first build (edges deferred by one frame)
  const isInitialBuild = useRef(true)

  // Track previously running nodes for auto-focus
  const prevRunningIdsRef = useRef<Set<string>>(new Set())

  // Debounce auto-focus: accumulate newly running nodes over a window before fitView
  const autoFocusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingFocusNodesRef = useRef<ReactFlowCanvasNode[]>([])

  // Explicit focus intents (from backend focus_node tool) take priority over auto-focus
  const lastFocusIntentTs = useRef(0)

  useEffect(() => {
    if (!synced || !isBackendConnected) {
      lifecycleLog('effect skip: synced=%s isBackendConnected=%s', synced, isBackendConnected)
      return
    }
    lifecycleLog('effect run: synced=%s isBackendConnected=%s', synced, isBackendConnected)

    isInitialBuild.current = true

    const yNodes = ydoc.getMap<YDocMap>('nodes')
    const yEdges = ydoc.getMap<YDocMap>('edges')
    yNodesRef.current = yNodes

    const readNodesFromYDoc = (): ReactFlowCanvasNode[] => {
      const nodes: ReactFlowCanvasNode[] = []
      yNodes.forEach((ynode: YDocMap, nodeId: string) => {
        // RFC-0319: read per-field keys
        const dataRaw = readNodeDataFromYMap(ynode)
        const parentId = yOptionalString(ynode, 'parentNode')
        const nodeType = yString(ynode, 'type')

        // Nodes marked canvasHidden in their schema live in YDoc for data
        // persistence but are never rendered on the canvas.
        if (CANVAS_HIDDEN_FE_TYPES.has(nodeType)) return

        // Skip node types not registered in the caller's schema registry
        if (knownNodeTypes && !knownNodeTypes.has(nodeType)) {
          return
        }

        // Group nodes need explicit dimensions so ReactFlow sizes the container.
        // For nodeGroup the width is component-driven (GroupNode computes
        // containerWidth from children layout); for webtoonGroup and
        // shortVideoGroup the width is taken directly from YDoc (the FE
        // measures and writes the rendered DOM width via writeNodeDimensions).
        const isGroup = nodeType === 'webtoonGroup' || nodeType === 'shortVideoGroup' || nodeType === 'nodeGroup'
        const isYDocSizedGroup = nodeType === 'webtoonGroup' || nodeType === 'shortVideoGroup'
        const collapsed = dataRaw.in_collapsed === true

        // Only set parentId if the parent exists in YDoc AND will be rendered.
        // A parent that exists in YDoc but is filtered out by knownNodeTypes
        // (e.g. legacy "group" after rename to "nodeGroup") would crash
        // ReactFlow with "Parent node ... not found".
        const parentExists = parentId ? (() => {
          const parentYNode = yNodes.get(parentId)
          if (!parentYNode) return false
          const parentType = yString(parentYNode, 'type')
          if (knownNodeTypes && !knownNodeTypes.has(parentType)) return false
          return true
        })() : false

        const posX = yNumber(ynode, 'x')
        const posY = yNumber(ynode, 'y')

        // Diagnostic logging for designer nodes — traces YDoc position data
        if (['characterDesigner', 'locationDesigner', 'objectDesigner'].includes(nodeType)) {
          layoutLog('[layout] readNodesFromYDoc: %s type=%s x=%d y=%d ydoc_w=%s ydoc_h=%s',
            nodeId, nodeType, posX, posY, ynode.get('width'), ynode.get('height'))
        }

        const node: ReactFlowCanvasNode = {
          id: nodeId,
          type: nodeType,
          position: { x: posX, y: posY },
          data: {
            ...dataRaw,
            status: yString(ynode, 'status', 'idle'),
            label: yString(ynode, 'label', nodeId),
            tainted: yBoolean(ynode, 'tainted'),
            ...(parentExists ? { isInsideGroup: true } : {}),
          },
          ...(parentExists ? { parentId } : {}),
          ...(isGroup ? {
            style: {
              width: isYDocSizedGroup ? yOptionalNumber(ynode, 'width') : undefined,
              height: collapsed ? 48 : yOptionalNumber(ynode, 'height'),
              padding: 0,
              backgroundColor: 'transparent',
              border: 'none',
            },
          } : {}),
        }

        // Stash the authoritative expanded height so collapse toggle can restore it
        if (isGroup) {
          const ydocHeight = yOptionalNumber(ynode, 'height')
          if (ydocHeight) {
            node.data._expandedHeight = ydocHeight
          }
        }

        // Child nodes inside a shortVideoGroup should be non-draggable (auto-layout)
        if (parentExists) {
          const parentYNode = yNodes.get(parentId!)
          if (parentYNode) {
            const parentType = yOptionalString(parentYNode, 'type')

            // Lock panels in horizontal layout groups (Figma-like auto-layout)
            if (parentType === 'shortVideoGroup') {
              node.draggable = false
            }
          }
        }

        nodes.push(node)
      })
      const derivedNodes = deriveGroupedNodeLayout(nodes)
      // ReactFlow requires parent nodes before children in the array
      derivedNodes.sort((a, b) => {
        if (a.parentId && !b.parentId) return 1
        if (!a.parentId && b.parentId) return -1
        return 0
      })
      return derivedNodes
    }

    // Build child→collapsedGroup mapping for edge remapping
    const getCollapsedGroupMap = (): Map<string, string> => {
      const childToGroup = new Map<string, string>()
      yNodes.forEach((ynode: YDocMap, nodeId: string) => {
        const parentId = yOptionalString(ynode, 'parentNode')
        if (!parentId) return
        const parentYNode = yNodes.get(parentId)
        if (!parentYNode) return
        const parentBackendType = toBackendType(yString(parentYNode, 'type'))
        const parentData = readYDocNodeDataFields(parentYNode, parentBackendType)
        if (parentData.in_collapsed === true) {
          childToGroup.set(nodeId, parentId)
        }
      })
      return childToGroup
    }

    const readEdgesFromYDoc = (): ReactFlowCanvasEdge[] => {
      const collapsedMap = getCollapsedGroupMap()
      const edges: ReactFlowCanvasEdge[] = []
      const seen = new Set<string>()
      yEdges.forEach((yedge: YDocMap, edgeId: string) => {
        let source = yString(yedge, 'source')
        let target = yString(yedge, 'target')
        const type = yString(yedge, 'type', 'default')
        let sourceHandle = yOptionalString(yedge, 'sourceHandle')
        let targetHandle = yOptionalString(yedge, 'targetHandle')

        // Remap edges involving children of collapsed groups
        if (collapsedMap.has(source)) {
          source = collapsedMap.get(source)!
          sourceHandle = undefined
        }
        if (collapsedMap.has(target)) {
          target = collapsedMap.get(target)!
          targetHandle = undefined
        }
        // Skip self-loops (both ends remapped to same group)
        if (source === target) return
        // Skip edges targeting group nodes — parent-child is via parentId, not edges
        const targetYNode = yNodes.get(target)
        if (targetYNode) {
          const targetType = yOptionalString(targetYNode, 'type')
          if (targetType === 'nodeGroup' || targetType === 'shortVideoGroup' || targetType === 'webtoonGroup') return
        }
        const toIndex = yOptionalNumber(yedge, 'toIndex')
        const fromIndex = yOptionalNumber(yedge, 'fromIndex')
        // Deduplicate exact rendered edges after collapsed-group remapping.
        const dedup = [
          source,
          sourceHandle ?? '',
          target,
          targetHandle ?? '',
          fromIndex ?? '',
          toIndex ?? '',
        ].join('→')
        if (seen.has(dedup)) return
        seen.add(dedup)

        const edge: ReactFlowCanvasEdge = { id: edgeId, source, target, type }
        if (sourceHandle) edge.sourceHandle = sourceHandle
        if (targetHandle) edge.targetHandle = targetHandle
        if (toIndex !== undefined) {
          edge.data = { ...edge.data, toIndex }
        }
        if (fromIndex !== undefined) {
          edge.data = { ...edge.data, fromIndex }
        }
        edges.push(edge)
      })
      return edges
    }

    const yClientID = ydoc.clientID

    const rebuild = (events?: YDocEvent[], source?: 'nodes' | 'edges'): void => {
      // During undo/redo, skip — the undo handler owns node state
      if (isUndoRedo.current) return

      const origin = events && events.length > 0 ? events[0].transaction.origin : 'initial'

      // Skip rebuild for our own frontend writes (e.g. config field edits) —
      // the frontend updated React state in the same tick, so re-reading
      // YDoc would clobber any in-flight edits.
      //
      // BUT only skip for DATA-ONLY frontend writes.  Structural frontend
      // writes (add/remove a node or edge via ``addNodeToYDoc`` /
      // ``removeNodeFromYDoc`` / ``addEdgeToYDoc`` / ``removeEdgeFromYDoc``)
      // do NOT update React state inline — they rely on this observer to
      // re-read YDoc and feed ReactFlow.  Short-circuiting structural
      // writes leaves the canvas visually stale until a page refresh
      // (delete button appears to be a no-op).  Structural events appear
      // as adds/deletes on the ``yNodes`` / ``yEdges`` parent map itself,
      // so target identity disambiguates them from per-node data edits.
      if (events && events.length > 0 && events[0].transaction.origin === 'frontend') {
        const yEdgesMap = ydoc.getMap('edges')
        const isStructural = events.some(e => {
          if (e.target !== yNodes && e.target !== yEdgesMap) return false
          const keys: Map<string, { action: string }> | undefined = e.changes?.keys
          if (!keys || keys.size === 0) return false
          for (const [, change] of keys) {
            if (change.action === 'add' || change.action === 'delete') return true
          }
          return false
        })
        if (!isStructural) {
          return
        }
        log('rebuild → NOT skipping frontend write (structural change detected) origin=%s', origin)
      }

      // --- Edge-only fast path ---
      // When only edges changed (add/remove/reorder), update edges without
      // touching nodes. Calling setNodes with new objects forces ReactFlow to
      // re-measure node dimensions, which briefly un-renders all edges.
      if (source === 'edges' && !isInitialBuild.current) {
        log('rebuild → EDGE-ONLY fast path (client=%d origin=%s)', yClientID, origin)
        setEdges(() => {
          const freshEdges = readEdgesFromYDoc()
          edgesRef.current = freshEdges
          return freshEdges
        })
        return
      }

      // --- Node data-only fast path (status/label/in_*/out_*) ---
      // Update React node data in place without rebuilding from scratch.
      // RFC-0319: per-field keys (in_*/out_*) are also non-structural data updates.
      const isNonStructuralKey = (key: string): boolean =>
        !YDOC_STRUCTURAL_KEYS.has(key) ||
        key === 'status' || key === 'label'
      const isDataOnlyUpdate = events && events.length > 0 && nodesRef.current.length > 0 && events.every(e => {
        if (!(e.target instanceof Y.Map)) return false
        const keys: Map<string, { action: string }> | undefined = e.changes?.keys
        if (!keys || keys.size === 0) return false
        for (const [key, change] of keys) {
          if (!isNonStructuralKey(key) || change.action !== 'update') return false
        }
        return true
      })
      const layoutSensitiveGroupIds = new Set<string>()
      if (events) {
        for (const e of events) {
          if (!(e.target instanceof Y.Map)) continue
          const keys: Map<string, { action: string }> | undefined = e.changes?.keys
          if (!keys || keys.size === 0) continue
          const changedNodeId = [...yNodes.entries()].find(([, v]) => v === e.target)?.[0]
          if (!changedNodeId) continue
          for (const [key] of keys) {
            if (key === 'in_child_node_ids' || key === 'in_layout_mode' || key === 'in_collapsed') {
              layoutSensitiveGroupIds.add(changedNodeId)
            }
          }
        }
      }

      // Debug: log status changes on any node
      if (events) {
        for (const e of events) {
          if (!(e.target instanceof Y.Map)) continue
          const keys: Map<string, { action: string; oldValue?: YDocValue }> | undefined = e.changes?.keys
          if (!keys) continue
          for (const [key, change] of keys) {
            if (key === 'status') {
              const ymap: YDocMap = e.target
              const nodeId = [...yNodes.entries()].find(([, v]) => v === ymap)?.[0] ?? '??'
              const newVal = ymap.get('status')
              const nodeType = yOptionalString(ymap, 'type')
              log('statusChange node=%s status: %s → %s (client=%d origin=%s)', nodeId, String(change.oldValue), String(newVal), yClientID, String(origin))
              statusLog('ydoc-change nodeId=%s nodeType=%s old=%s new=%s origin=%s', nodeId, nodeType ?? '??', String(change.oldValue), String(newVal), String(origin))
            }
          }
        }
      }

      // Count running and queued non-director nodes across ALL YDoc nodes (including canvas-hidden types).
      // Must run on every update (data-only AND full rebuild) so the task banner stays current.
      {
        let runCount = 0
        let queuedCount = 0
        const runningIds: string[] = []
        yNodes.forEach((ynode: YDocMap, nodeId: string) => {
          const st = yOptionalString(ynode, 'status')
          const tp = yOptionalString(ynode, 'type')
          if (tp === 'director') return
          if (st === 'running') {
            runCount++
            runningIds.push(`${nodeId}(${tp})`)
          } else if (st === 'pending_execution') {
            queuedCount++
          }
        })
        log('runCount running=%d queued=%d %o', runCount, queuedCount, runningIds.length > 0 ? runningIds : '(none)')
        setYdocRunningCount(runCount)
        setYdocQueuedCount(queuedCount)
      }

      if (isDataOnlyUpdate) {
        log('rebuild → DATA-ONLY fast path (client=%d origin=%s)', yClientID, origin)
        // Use functional updater to avoid race with useEffect(() => nodesRef.current = nodes).
        // The useEffect can clobber nodesRef with stale React state between renders;
        // functional updaters always receive React's latest pending state.
        setNodes(prevNodes => {
          // Compare against visible YDoc nodes only. Hidden types such as
          // director live in YDoc but are never rendered.
          let visibleYNodeCount = 0
          yNodes.forEach((_ynode: YDocMap, _nodeId: string) => {
            const nt = yString(_ynode, 'type')
            if (!CANVAS_HIDDEN_FE_TYPES.has(nt) && (!knownNodeTypes || knownNodeTypes.has(nt))) {
              visibleYNodeCount++
            }
          })
          if (prevNodes.length === visibleYNodeCount) {
            log('rebuild DATA-ONLY: count match (%d), merging in-place', prevNodes.length)
            // First pass: merge data from YDoc
            const updatedNodes = prevNodes.map(rfNode => {
              const ynode = yNodes.get(rfNode.id)
              if (!ynode) return rfNode

              // RFC-0319: read per-field keys with data_json fallback
              const dataRaw = readNodeDataFromYMap(ynode)

              const newStatus = yString(ynode, 'status', 'idle')
              const newLabel = yString(ynode, 'label', rfNode.id)

              const merged: ReactFlowCanvasNode = {
                ...rfNode,
                data: {
                  ...rfNode.data,
                  ...dataRaw,
                  status: newStatus,
                  label: newLabel,
                },
              }

              return merged
            })

            // Second pass: sync child hidden state with parent collapse
            const groupCollapseMap = new Map<string, boolean>()
            for (const n of updatedNodes) {
              if (n.type === 'webtoonGroup' || n.type === 'nodeGroup') {
                groupCollapseMap.set(n.id, n.data?.in_collapsed === true)
              }
            }
            const groupLayoutNodes = layoutSensitiveGroupIds.size > 0
              ? deriveGroupedNodeLayout(updatedNodes, layoutSensitiveGroupIds)
              : updatedNodes
            const finalNodes = groupLayoutNodes.map(n => {
              if (n.parentId && groupCollapseMap.has(n.parentId)) {
                const parentCollapsed = groupCollapseMap.get(n.parentId)!
                if (n.hidden !== parentCollapsed) {
                  return { ...n, hidden: parentCollapsed }
                }
              }
              return n
            })

            nodesRef.current = finalNodes
            return finalNodes
          }

          // Mismatch: prevNodes is stale — rebuild from YDoc so the update
          // is not silently dropped.
          log('rebuild DATA-ONLY: COUNT MISMATCH prevNodes=%d visibleYNodes=%d (total=%d) — falling back to full rebuild', prevNodes.length, visibleYNodeCount, yNodes.size)
          const rebuilt = applySelection(readNodesFromYDoc(), selectedNodeIdsRef)
          nodesRef.current = rebuilt
          return rebuilt
        })

        _autoFocus(nodesRef.current)
        return
      }

      // --- Full rebuild path: structural node changes (add/remove nodes) ---
      // Use functional updaters that re-read YDoc at React flush time. When YDoc
      // fires rapid add/remove events, React queues each setNodes() call. Direct
      // calls like setNodes(snapshot) freeze state at event time, but by the time
      // React processes the queue earlier snapshots overwrite later ones — causing
      // the "oscillating node count" bug. Functional updaters always read the
      // latest YDoc state when React actually applies the update.

      if (!isInitialBuild.current) {
        log('rebuild → FULL REBUILD (structural change), yNodes.size=%d (client=%d origin=%s)', yNodes.size, yClientID, origin)
        setNodes((_prevNodes) => {
          const freshNodes = readNodesFromYDoc()
          lifecycleLog('full rebuild: freshNodes=%d prevNodes=%d origin=%s', freshNodes.length, _prevNodes.length, origin)

          if (freshNodes.length === 0 && isInitialBuild.current) return _prevNodes
          const result = applySelection(freshNodes, selectedNodeIdsRef)

          nodesRef.current = result
          return result
        })
        setEdges(() => {
          const freshEdges = readEdgesFromYDoc()
          edgesRef.current = freshEdges
          return freshEdges
        })
        setReady(true)
      } else {
        isInitialBuild.current = false
        setNodes((prevNodes) => {
          const freshNodes = readNodesFromYDoc()
          lifecycleLog('initial build: freshNodes=%d prevNodes=%d', freshNodes.length, prevNodes.length)
          nodesRef.current = freshNodes
          return freshNodes
        })
        setEdges([])

        requestAnimationFrame(() => {
          setEdges(() => {
            const freshEdges = readEdgesFromYDoc()
            edgesRef.current = freshEdges
            return freshEdges
          })
          setReady(true)
        })
      }

      _autoFocus(readNodesFromYDoc())
    }

    const _autoFocus = (nodes: ReactFlowCanvasNode[]) => {
      const runningNodes = nodes.filter(n => {
        const status = typeof n.data?.status === 'string' ? n.data.status : undefined
        return status === 'running'
      })
      const currentRunningIds = new Set(runningNodes.map(n => n.id))
      const newlyRunning = runningNodes.filter(n => !prevRunningIdsRef.current.has(n.id))
      prevRunningIdsRef.current = currentRunningIds

      if (newlyRunning.length === 0 || !reactFlowInstance.current) return

      // Explicit focus_node intents take priority — skip auto-focus if one was
      // received recently so we don't override the user-facing pan animation.
      if (Date.now() - lastFocusIntentTs.current < 2000) return

      // Accumulate newly running nodes and debounce the fitView call.
      // During batch generation (e.g. 9 storyboard panels), this prevents
      // the viewport from jumping to each panel individually.
      pendingFocusNodesRef.current.push(...newlyRunning)

      if (autoFocusTimerRef.current) {
        clearTimeout(autoFocusTimerRef.current)
      }

      autoFocusTimerRef.current = setTimeout(() => {
        const pending = pendingFocusNodesRef.current
        pendingFocusNodesRef.current = []
        autoFocusTimerRef.current = null

        if (pending.length > 0 && reactFlowInstance.current) {
          reactFlowInstance.current.fitView({
            nodes: pending,
            padding: 0.15,
            duration: 400,
            maxZoom: 1,
            minZoom: 0.3,
          })
        }
      }, 500)
    }

    // Initial build + observe both maps with source tags
    rebuild()
    const onNodesChange = (events: YDocEvent[]) => rebuild(events, 'nodes')
    const onEdgesChange = (events: YDocEvent[]) => rebuild(events, 'edges')
    yNodes.observeDeep(onNodesChange)
    yEdges.observeDeep(onEdgesChange)

    // --- Focus intent observer ---
    const yFocus = ydoc.getMap('focus')
    const scheduleFitView = (nodeId: string, attempt: number) => {
      const target = nodesRef.current.find(n => n.id === nodeId)
      if (!target) {
        if (attempt < 3) {
          log('focus: node %s not in React state yet, retry %d/3', nodeId, attempt + 1)
          setTimeout(() => scheduleFitView(nodeId, attempt + 1), 500)
        } else {
          log('focus: node %s not found after 3 retries, giving up', nodeId)
        }
        return
      }
      const rf = reactFlowInstance.current
      if (!rf) {
        log('focus: no reactFlowInstance, skipping')
        return
      }

      // Focus on the exact node requested. fitView resolves absolute
      // positions through ReactFlow's parent chain, so child nodes inside
      // groups work without the relative-position bug that setCenter used
      // to hit. Earlier we redirected child focus to the parent group, but
      // the Director's intent when calling focus_node on a specific clip /
      // panel / image is precisely to highlight that one node — redirecting
      // to the group was reading as "viewport never lands on the new asset"
      // during per-clip generation.
      log('focus: fitting view on %s', nodeId)
      rf.fitView({
        nodes: [{ id: nodeId }],
        padding: 0.15,
        duration: 500,
        maxZoom: 1,
        minZoom: 0.3,
      })
    }

    let pendingFocusIntent: string | null = null
    let focusIntentTimer: ReturnType<typeof setTimeout> | null = null

    const onFocusChange = () => {
      const nodeIdValue = yFocus.get('nodeId')
      const nodeId = typeof nodeIdValue === 'string' ? nodeIdValue : undefined
      if (!nodeId || !reactFlowInstance.current) {
        log('focus: observer fired but no nodeId or no reactFlowInstance')
        return
      }

      lastFocusIntentTs.current = Date.now()
      log('focus: intent received for node %s', nodeId)

      // Debounce: when multiple focus intents arrive rapidly (e.g. 9 panel
      // completions in a storyboard), only the last one triggers viewport
      // movement. This prevents the viewport from jumping 9 times.
      pendingFocusIntent = nodeId
      if (focusIntentTimer) {
        clearTimeout(focusIntentTimer)
      }
      focusIntentTimer = setTimeout(() => {
        const pending = pendingFocusIntent
        pendingFocusIntent = null
        focusIntentTimer = null
        if (pending) {
          scheduleFitView(pending, 0)
        }
      }, 500)
    }
    yFocus.observe(onFocusChange)

    return () => {
      lifecycleLog('effect cleanup: detaching observers')
      yNodes.unobserveDeep(onNodesChange)
      yEdges.unobserveDeep(onEdgesChange)
      yFocus.unobserve(onFocusChange)
      yNodesRef.current = null
      if (autoFocusTimerRef.current) {
        clearTimeout(autoFocusTimerRef.current)
        autoFocusTimerRef.current = null
      }
      if (focusIntentTimer) {
        clearTimeout(focusIntentTimer)
        focusIntentTimer = null
      }
    }
  }, [ydoc, synced, isBackendConnected, setNodes, setEdges, nodesRef, edgesRef, isUndoRedo, reactFlowInstance, knownNodeTypes, selectedNodeIdsRef])

  // --- Write helpers ---

  const writeNodePosition = useCallback((nodeId: string, x: number, y: number, tainted?: boolean) => {
    const yNodes = yNodesRef.current
    if (!yNodes) {
      lifecycleLog('writeNodePosition: yNodesRef is null, skipping %s', nodeId)
      return
    }

    const ynode = yNodes.get(nodeId)
    if (!ynode) {
      lifecycleLog('writeNodePosition: node not found in Y doc, skipping %s', nodeId)
      return
    }

    // Skip position writes for group children — their positions are computed
    // from in_child_node_ids order in readNodesFromYDoc, never stored.
    if (ynode.get('parentNode')) return

    log('writePosition node=%s x=%d y=%d tainted=%s (client=%d)', nodeId, x, y, tainted, ydoc.clientID)
    statusLog('position-write nodeId=%s x=%d y=%d source=drag', nodeId, x, y)
    const curX = yOptionalNumber(ynode, 'x')
    const curY = yOptionalNumber(ynode, 'y')
    if (curX === x && curY === y && (tainted === undefined || ynode.get('tainted') === tainted)) return
    ydoc.transact(() => {
      ynode.set('x', x)
      ynode.set('y', y)
      if (tainted !== undefined) {
        ynode.set('tainted', tainted)
      }
    }, 'frontend')
  }, [ydoc])

  const addNodeToYDoc = useCallback((
    nodeId: string,
    nodeType: string,
    label: string,
    x: number,
    y: number,
    data?: NodeDataUpdates,
    tainted?: boolean,
    parentId?: string,
  ) => {
    const yNodes = yNodesRef.current
    if (!yNodes) {
      lifecycleLog('addNodeToYDoc: yNodesRef is null, skipping %s', nodeId)
      return
    }

    if (yNodes.has(nodeId)) {
      return
    }

    ydoc.transact(() => {
      const ynode = new Y.Map<YDocValue>()
      ynode.set('type', nodeType)
      ynode.set('label', label)
      const initialData = data ? normalizeNodeDataUpdates(data) : undefined
      const resolvedStatus = typeof initialData?.status === 'string' ? initialData.status : 'idle'
      ynode.set('status', resolvedStatus)
      if (resolvedStatus !== 'idle') {
        libraryImportLog('addNodeToYDoc preserving non-idle status %s for node %s (type=%s)', resolvedStatus, nodeId, nodeType)
      }
      ynode.set('x', x)
      ynode.set('y', y)
      ynode.set('tainted', !!tainted)
      if (parentId) {
        ynode.set('parentNode', parentId)
      }
      // Write initial data as per-field keys
      if (initialData) {
        const backendType = toBackendType(nodeType)
        writeYDocNodeDataFields(ynode, backendType, initialData)
      }
      yNodes.set(nodeId, ynode)
    }, 'frontend')
  }, [ydoc])

  const removeNodeFromYDoc = useCallback((nodeId: string) => {
    const yNodes = yNodesRef.current
    if (!yNodes || !yNodes.has(nodeId)) return
    const yEdges = ydoc.getMap<YDocMap>('edges')
    ydoc.transact(() => {
      const affectedPorts: TargetPortRef[] = []
      // Remove all edges connected to this node
      yEdges.forEach((_yedge: YDocMap, edgeId: string) => {
        const source = yString(_yedge, 'source')
        const target = yString(_yedge, 'target')
        if (source === nodeId || target === nodeId) {
          if (source === nodeId && target !== nodeId) {
            rememberTargetPort(affectedPorts, target, yOptionalString(_yedge, 'targetHandle'))
          }
          yEdges.delete(edgeId)
        }
      })
      for (const port of affectedPorts) {
        reindexTargetPortEdges(yEdges, port.target, port.targetHandle)
      }
      yNodes.delete(nodeId)
    }, 'frontend')
  }, [ydoc])

  const addEdgeToYDoc = useCallback((edge: { source: string; target: string; sourceHandle?: string; targetHandle?: string; toIndex?: number; fromIndex?: number; type?: string }) => {
    const yEdges = ydoc.getMap<YDocMap>('edges')
    const indexSuffix = edge.toIndex !== undefined ? `[${edge.toIndex}]` : ''
    const edgeId = edge.sourceHandle && edge.targetHandle
      ? `${edge.source}.${edge.sourceHandle}-${edge.target}.${edge.targetHandle}${indexSuffix}`
      : `${edge.source}-${edge.target}${indexSuffix}`
    ydoc.transact(() => {
      const edgeData = new Y.Map<YDocValue>()
      edgeData.set('source', edge.source)
      edgeData.set('target', edge.target)
      edgeData.set('type', edge.type ?? 'default')
      if (edge.sourceHandle) edgeData.set('sourceHandle', edge.sourceHandle)
      if (edge.targetHandle) edgeData.set('targetHandle', edge.targetHandle)
      if (edge.toIndex !== undefined) edgeData.set('toIndex', edge.toIndex)
      if (edge.fromIndex !== undefined) edgeData.set('fromIndex', edge.fromIndex)
      yEdges.set(edgeId, edgeData)
    }, 'frontend')
  }, [ydoc])

  const removeEdgeFromYDoc = useCallback((edgeId: string) => {
    const yEdges = ydoc.getMap<YDocMap>('edges')
    ydoc.transact(() => {
      const yedge = yEdges.get(edgeId)
      if (!yedge) return
      const target = yString(yedge, 'target')
      const targetHandle = yOptionalString(yedge, 'targetHandle')
      yEdges.delete(edgeId)
      reindexTargetPortEdges(yEdges, target, targetHandle)
    }, 'frontend')
  }, [ydoc])

  const removeEdgesFromYDocByPorts = useCallback((edges: Array<{ source: string; target: string; sourceHandle?: string; targetHandle?: string }>) => {
    const yEdges = ydoc.getMap<YDocMap>('edges')
    ydoc.transact(() => {
      const affectedPorts: TargetPortRef[] = []
      for (const e of edges) {
        const toDelete: string[] = []
        yEdges.forEach((_yedge: YDocMap, edgeId: string) => {
          const src = yString(_yedge, 'source')
          const tgt = yString(_yedge, 'target')
          const srcH = yOptionalString(_yedge, 'sourceHandle')
          const tgtH = yOptionalString(_yedge, 'targetHandle')
          if (src === e.source && tgt === e.target &&
              (!e.sourceHandle || srcH === e.sourceHandle) &&
              (!e.targetHandle || tgtH === e.targetHandle)) {
            rememberTargetPort(affectedPorts, tgt, tgtH)
            toDelete.push(edgeId)
          }
        })
        for (const edgeId of toDelete) {
          yEdges.delete(edgeId)
        }
      }
      for (const port of affectedPorts) {
        reindexTargetPortEdges(yEdges, port.target, port.targetHandle)
      }
    }, 'frontend')
  }, [ydoc])

  const writeNodeData = useCallback((nodeId: string, updates: NodeDataUpdates) => {
    const yNodes = yNodesRef.current
    if (!yNodes) return

    const ynode = yNodes.get(nodeId)
    if (!ynode) return

    const feType = yString(ynode, 'type')
    const backendType = toBackendType(feType)
    const updatesToWrite = withSynchronizedVariantPointers(normalizeNodeDataUpdates(updates), ynode, nodeId)

    ydoc.transact(() => {
      for (const key of writeYDocNodeDataFields(ynode, backendType, updatesToWrite)) {
        ydocWriteLog('write nodeId=%s key=%s', nodeId, key)
      }

      // Promote well-known fields to top-level YDoc keys
      if ('name' in updatesToWrite) {
        const v = updatesToWrite.name
        if (typeof v === 'string') {
          if (ynode.get('label') !== v) {
            ynode.set('label', v)
            ydocWriteLog('write nodeId=%s key=%s', nodeId, 'label (from name)')
          } else {
            ydocWriteLog('skip same-value nodeId=%s key=%s', nodeId, 'label (from name)')
          }
        }
      }
      if ('label' in updatesToWrite && typeof updatesToWrite.label === 'string') {
        if (ynode.get('label') !== updatesToWrite.label) {
          ynode.set('label', updatesToWrite.label)
          ydocWriteLog('write nodeId=%s key=%s', nodeId, 'label')
        } else {
          ydocWriteLog('skip same-value nodeId=%s key=%s', nodeId, 'label')
        }
      }
      if ('status' in updatesToWrite && typeof updatesToWrite.status === 'string') {
        if (ynode.get('status') !== updatesToWrite.status) {
          ynode.set('status', updatesToWrite.status)
          ydocWriteLog('write nodeId=%s key=%s', nodeId, 'status')
        } else {
          ydocWriteLog('skip same-value nodeId=%s key=%s', nodeId, 'status')
        }
      }
    }, 'frontend')
  }, [ydoc])

  const flushNodeInputFromEdgeRefs = useCallback((
    targetId: string,
    targetHandle: string,
    nodes: readonly YDocInputFlushNode[],
    edgeRefs: readonly YDocInputFlushEdgeRef[],
  ): YDocInputFlushResult | undefined => {
    const yNodes = yNodesRef.current
    if (!yNodes) return undefined

    const ynode = yNodes.get(targetId)
    if (!ynode) return undefined

    const targetNode = nodes.find((node) => node.id === targetId)
    if (!targetNode) return undefined

    let result: YDocInputFlushResult | undefined
    ydoc.transact(() => {
      result = flushYDocInputFromEdgeRefs(ynode, targetNode, targetHandle, nodes, edgeRefs)
      if (result?.written) {
        ydocWriteLog('generated flush nodeId=%s key=%s', targetId, result.field)
      }
    }, 'frontend')
    return result
  }, [ydoc])

  const writeNodeDimensions = useCallback((nodeId: string, width: number, height: number) => {
    const yNodes = yNodesRef.current
    if (!yNodes) return

    const ynode = yNodes.get(nodeId)
    if (!ynode) return

    // Frontend reports rendered dimensions for all node types, including
    // groups. Backend layout treats schema dimensions as the minimum reserved
    // footprint and only expands from these measurements, so progressive render
    // states cannot shrink the space reserved for downstream nodes.
    // See _canvas_utils._create_group_node for the one intentional asymmetry
    // where the user-grouping path seeds an initial size to avoid a visible
    // flash; that seed is overwritten by this function on the next layout cycle.
    const ynodeType = yString(ynode, 'type')

    // Round to integers to prevent sub-pixel jitter loops
    const w = Math.round(width)
    const h = Math.round(height)
    const curW = yOptionalNumber(ynode, 'width')
    const curH = yOptionalNumber(ynode, 'height')
    if (curW === w && curH === h) return

    layoutLog('[size] writeNodeDimensions: %s type=%s w=%d h=%d prev_w=%s prev_h=%s',
      nodeId, ynodeType, w, h, curW, curH)

    ydoc.transact(() => {
      ynode.set('width', w)
      ynode.set('height', h)
    }, 'frontend')
  }, [ydoc])

  const writeNodeParent = useCallback((nodeId: string, parentId: string | null) => {
    const yNodes = yNodesRef.current
    if (!yNodes) return

    const ynode = yNodes.get(nodeId)
    if (!ynode) return

    ydoc.transact(() => {
      if (parentId) {
        ynode.set('parentNode', parentId)
      } else {
        ynode.delete('parentNode')
      }
    }, 'frontend')
  }, [ydoc])

  // Cross-instance node status sync: listen for dw:node-status events
  // dispatched by the chat WebSocket handler (via Redis Pub/Sub) and apply
  // the status directly to the local YDoc so the canvas updates.
  //
  // Also: when the status is terminal (completed / error) for a
  // non-director node, invalidate the cost-summary React Query entry so
  // the Sparks & Usage modal's Node Breakdown reflects the latest run
  // without a page refresh. Backend broadcasts `node_status` for every
  // status change in single-instance dev too, so this is the canonical
  // hook for both local and multi-instance behaviour.
  useEffect(() => {
    const handler = (e: Event) => {
      if (!(e instanceof CustomEvent) || !isRecord(e.detail)) return
      const nodeId = typeof e.detail.nodeId === 'string' ? e.detail.nodeId : undefined
      const status = typeof e.detail.status === 'string' ? e.detail.status : undefined
      const hasError = Object.prototype.hasOwnProperty.call(e.detail, 'error')
      const error =
        typeof e.detail.error === 'string'
          ? e.detail.error
          : e.detail.error === null
            ? null
            : undefined
      if (!nodeId || !status) return
      const yNodes = yNodesRef.current
      if (!yNodes) return
      const ynode = yNodes.get(nodeId)
      if (!ynode) return
      if (ynode.get('status') === status && (!hasError || ynode.get('error') === error)) return
      const nodeType = yOptionalString(ynode, 'type')
      ydoc.transact(() => {
        ynode.set('status', status)
        if (hasError) {
          ynode.set('error', error ?? null)
        }
      }, 'backend-relay')

      // Cost summary invalidation — terminal-only, non-director, project-scoped.
      const isTerminal = status === 'completed' || status === 'error'
      if (!isTerminal || nodeType === 'director' || !projectId) {
        if (isTerminal && !projectId) {
          costInvalidateLog({ event: 'skipped', reason: 'no-projectId', nodeId, nodeType, status })
        }
        return
      }
      costInvalidateBatchedRef.current += 1
      costInvalidateLog({
        event: 'schedule',
        projectId,
        source: 'ws-relay',
        nodeId,
        nodeType,
        newStatus: status,
        batched: costInvalidateBatchedRef.current,
      })
      if (costInvalidateTimerRef.current) {
        clearTimeout(costInvalidateTimerRef.current)
      }
      costInvalidateTimerRef.current = setTimeout(() => {
        const batchedCount = costInvalidateBatchedRef.current
        costInvalidateBatchedRef.current = 0
        costInvalidateTimerRef.current = null
        costInvalidateLog({ event: 'invalidate', projectId, batchedCount })
        queryClient.invalidateQueries({ queryKey: queryKeys.costSummary(projectId) })
      }, 250)
    }
    window.addEventListener('dw:node-status', handler)
    return () => {
      window.removeEventListener('dw:node-status', handler)
      if (costInvalidateTimerRef.current) {
        clearTimeout(costInvalidateTimerRef.current)
        costInvalidateTimerRef.current = null
      }
    }
  }, [ydoc, projectId, queryClient])

  return { ready, ydocRunningCount, ydocQueuedCount, writeNodePosition, addNodeToYDoc, removeNodeFromYDoc, writeNodeData, flushNodeInputFromEdgeRefs, writeNodeDimensions, writeNodeParent, addEdgeToYDoc, removeEdgeFromYDoc, removeEdgesFromYDocByPorts }
}

// ---------------------------------------------------------------------------
// useYViewport: Y doc → Viewport + write helper
// ---------------------------------------------------------------------------

interface UseYViewportResult {
  /** Viewport from the Y document (null until synced) */
  viewport: Viewport | null
  /** Write viewport changes back to the Y document */
  writeViewport: (viewport: Viewport) => void
}

export function useYViewport(ydoc: Y.Doc, synced: boolean): UseYViewportResult {
  const [viewport, setViewport] = useState<Viewport | null>(null)
  const yViewportRef = useRef<Y.Map<number> | null>(null)
  const suppressRef = useRef(false)

  useEffect(() => {
    if (!synced) return

    const yViewport = ydoc.getMap<number>('viewport')
    yViewportRef.current = yViewport

    const rebuild = () => {
      if (suppressRef.current) {
        suppressRef.current = false
        return
      }
      const x = yViewport.get('x') ?? 0
      const y = yViewport.get('y') ?? 0
      const zoom = yViewport.get('zoom') ?? 1
      setViewport({ x, y, zoom })
    }

    rebuild()
    yViewport.observeDeep(rebuild)

    return () => {
      yViewport.unobserveDeep(rebuild)
      yViewportRef.current = null
    }
  }, [ydoc, synced])

  const writeViewport = useCallback((vp: Viewport) => {
    const yViewport = yViewportRef.current
    if (!yViewport) return

    suppressRef.current = true
    ydoc.transact(() => {
      yViewport.set('x', vp.x)
      yViewport.set('y', vp.y)
      yViewport.set('zoom', vp.zoom)
    }, 'frontend')
  }, [ydoc])

  return { viewport, writeViewport }
}
